import { EnvironmentProviders } from '@angular/core';
import { provideFirestore, getFirestore, Firestore, connectFirestoreEmulator } from '@angular/fire/firestore';
import { provideAuth, getAuth, Auth } from '@angular/fire/auth';
import { connectAuthEmulator } from 'firebase/auth';
import { getApp, FirebaseApp } from '@angular/fire/app';
import { environment } from '../../../environments/environment';

/**
 * Configuración del emulador de Firebase para desarrollo local.
 * 
 * IMPORTANTE: El emulador de Firestore requiere reglas de seguridad configuradas.
 * Asegúrate de que tu emulador de Firebase esté configurado con reglas permisivas
 * para desarrollo. Puedes usar el archivo firestore.rules en la raíz del proyecto
 * como referencia para configurar reglas permisivas en tu emulador.
 * 
 * Para configurar las reglas en el emulador, ejecuta:
 * firebase emulators:start --only firestore --import=./firestore-data --export-on-exit
 * 
 * O configura las reglas en tu docker-compose o configuración del emulador.
 */

function configureFirestoreEmulator(firestore: Firestore, app: FirebaseApp): void {
  if (!environment.firebaseEmulator?.enabled) {
    console.warn('[Firebase Emulator] Firestore emulator configuration skipped - emulator disabled or config missing');
    return;
  }

  const emulatorConfig = environment.firebaseEmulator;
  const host = emulatorConfig.host || 'localhost';
  const port = emulatorConfig.firestore.port;

  try {
    connectFirestoreEmulator(firestore, host, port);
  } catch (error) {
    console.error('[Firebase Emulator] Firestore emulator connection failed:', error);
  }
}

function configureAuthEmulator(auth: Auth): void {
  if (!environment.firebaseEmulator?.enabled) {
    console.warn('[Firebase Emulator] Auth emulator configuration skipped - emulator disabled or config missing');
    return;
  }

  const emulatorConfig = environment.firebaseEmulator;
  const host = emulatorConfig.host || 'localhost';
  const authUrl = `http://${host}:${emulatorConfig.auth.port}`;

  try {
    connectAuthEmulator(auth, authUrl, {
      disableWarnings: true,
    });
  } catch (error) {
    console.error('[Firebase Emulator] Auth emulator connection failed:', error);
  }
}

export function provideFirebaseServices(): EnvironmentProviders[] {
  const shouldUseEmulator = !environment.production && environment.firebaseEmulator?.enabled;
  
  return [
    provideFirestore(() => {
      const app = getApp();
      
      const databaseId = shouldUseEmulator ? environment.firebase.projectId : undefined;
      const firestore = databaseId ? getFirestore(app, databaseId) : getFirestore(app);
      
      if (shouldUseEmulator) {
        configureFirestoreEmulator(firestore, app);
      }
      
      return firestore;
    }),
    provideAuth(() => {
      const auth = getAuth();
      
      if (shouldUseEmulator) {
        configureAuthEmulator(auth);
      }
      
      return auth;
    }),
  ];
}

